import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { httpBatchLink, httpLink, isNonJsonSerializable, retryLink, splitLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import superjson from 'superjson';

import type { AppRouter } from '@tagadapay/trpc-types';
import { config } from '../config';
import { authHeaders, resolveAuthToken, shouldRetryUnauthorized, type GetToken } from './auth-token';

// Create tRPC React client
export const trpc = createTRPCReact<AppRouter>();

export function createTRPCClient(getToken: GetToken) {
    // Per-operation refresh intent: when retryLink replays a 401'd operation we
    // record its id here so *that* operation's next token resolution bypasses
    // Clerk's cache. Keyed per op (not a shared flag) so concurrent in-flight
    // requests can't steal each other's refresh.
    const refreshOpIds = new Set<number>();

    const headersFor = async (ids: number[]): Promise<Record<string, string>> => {
        let forceRefresh = false;
        for (const id of ids) {
            if (refreshOpIds.delete(id)) forceRefresh = true;
        }
        return authHeaders(await resolveAuthToken(getToken, forceRefresh));
    };

    return trpc.createClient({
        links: [
            retryLink({
                retry: ({ error, op, attempts }) => {
                    if (!shouldRetryUnauthorized(error, attempts)) return false;
                    refreshOpIds.add(op.id);
                    return true;
                },
                retryDelayMs: () => 0,
            }),
            splitLink({
                condition: (op) => isNonJsonSerializable(op.input),
                true: httpLink({
                    transformer: {
                        serialize: (data) => data,
                        deserialize: superjson.deserialize,
                    },
                    url: `${config.apiBaseUrl}/api/crm/trpc`,
                    headers: ({ op }) => headersFor([op.id]),
                }),
                false: httpBatchLink({
                    url: `${config.apiBaseUrl}/api/crm/trpc`,
                    transformer: superjson,
                    headers: ({ opList }) => headersFor(opList.map((o) => o.id)),
                }),
            }),
        ],
    });
}


const CACHE_KEY = 'tgd-query-cache';

export const useResetQueries = () => {
    const utils = trpc.useUtils();
    const queryClient = useQueryClient();

    const reset = () => {
        utils.invalidate(undefined, { refetchType: 'none' });
    };

    /** Drop cached org data immediately so the UI never flashes the previous tenant. */
    const clearForOrgSwitch = useCallback(() => {
        void queryClient.cancelQueries();
        queryClient.removeQueries();
        try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
    }, [queryClient]);

    return { reset, clearForOrgSwitch };
};